// CSS 클래스 기반으로 수정된 App.js
import React, { useState, useEffect } from 'react';
import './App.css';

// API 서비스
const API_BASE = '/api/v1';

const api = {
  async request(endpoint) {
    try {
      const response = await fetch(`${API_BASE}${endpoint}`);
      if (!response.ok) {
        console.error(`API Error: ${endpoint} returned ${response.status}`);
        if (response.status === 500) {
          console.error('백엔드 서버 오류 - 데이터베이스 연결을 확인하세요');
        }
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error.message);
      return null;
    }
  },

  async testConnection() {
    try {
      const response = await fetch('http://localhost:8000/');
      return response.ok;
    } catch (error) {
      console.error('백엔드 연결 실패:', error);
      return false;
    }
  },

  getStats: () => api.request('/stats/'),
  getAIStatus: () => api.request('/health/ai'),
  getArticles: () => api.request('/articles/')
};

// 카테고리 색상 함수
const getCategoryColor = (category) => {
  const colors = {
    '정책': '#3b82f6',
    '인사': '#10b981',
    '선거': '#f59e0b',
    '논란': '#ef4444',
    '기타': '#6b7280'
  };
  return colors[category] || colors['기타'];
};

function App() {
  const [stats, setStats] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [backendConnected, setBackendConnected] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      const isConnected = await api.testConnection();
      setBackendConnected(isConnected);

      if (!isConnected) {
        console.warn('백엔드에 연결할 수 없습니다.');
        setLoading(false);
        return;
      }

      const [statsData, aiData, articlesData] = await Promise.all([
        api.getStats(),
        api.getAIStatus(),
        api.getArticles()
      ]);

      if (statsData) setStats(statsData);
      if (aiData) setAiStatus(aiData);
      if (articlesData) {
        setArticles(Array.isArray(articlesData) ? articlesData : []);
      }

      setLoading(false);
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>AI 분석 시스템을 불러오는 중...</p>
      </div>
    );
  }

  if (!backendConnected) {
    return (
      <div className="loading">
        <div style={{ fontSize: '3rem' }}>⚠️</div>
        <h2 style={{ color: '#e74c3c' }}>백엔드 서버에 연결할 수 없습니다</h2>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <p>다음을 확인해주세요:</p>
          <ul style={{ textAlign: 'left', marginTop: '1rem' }}>
            <li>백엔드 서버가 <code>http://localhost:8000</code>에서 실행 중인지 확인</li>
            <li>MySQL 데이터베이스가 실행 중인지 확인</li>
            <li><code>.env</code> 파일의 데이터베이스 설정 확인</li>
          </ul>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => window.location.reload()}
        >
          🔄 다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="app">
      {/* 헤더 */}
      <header className="header">
        <div className="header-content">
          <div className="header-title">
            <h1>PoliMeta</h1>
            <p>정치기사 메타데이터 생성기</p>
          </div>
        </div>
      </header>

      {/* 메인 컨테이너 */}
      <main className="main-container">
        {/* 탭 네비게이션 */}
        <div className="tabs">
          {[
            { id: 'dashboard', label: '📊 대시보드' },
            { id: 'articles', label: '📰 기사 목록' },
            { id: 'create', label: '✏️ 기사 작성' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 탭 컨텐츠 */}
        <div className="card">
          {activeTab === 'dashboard' && (
            <DashboardContent stats={stats} />
          )}

          {activeTab === 'articles' && (
            <div>
              <h2>📰 기사 목록 ({articles.length}개)</h2>
              {articles.length === 0 ? (
                <div className="empty-state">
                  등록된 기사가 없습니다.
                </div>
              ) : (
                <div>
                  {articles.slice(0, 10).map(article => (
                    <div key={article.id} className="article-item">
                      <h4 className="article-title">{article.title}</h4>
                      <p className="article-summary">
                        {article.summary || article.content?.substring(0, 200) + '...'}
                      </p>

                      <div className="article-meta">
                        <span
                          className="category-badge"
                          style={{ backgroundColor: getCategoryColor(article.category) }}
                        >
                          {article.category}
                        </span>
                        {article.source && <span>출처: {article.source}</span>}
                        <span>{new Date(article.created_at).toLocaleDateString()}</span>
                      </div>

                      {/* AI 생성 메타데이터 표시 */}
                      <div className="article-metadata">
                        {/* AI 생성 태그 */}
                        {article.tags && article.tags.length > 0 && (
                          <div className="metadata-section">
                            <span className="metadata-label">🏷️ AI 태그:</span>
                            <div className="tags">
                              {article.tags.slice(0, 8).map((tag) => (
                                <span
                                  key={tag.id}
                                  className="tag tag-blue clickable"
                                  onClick={() => console.log('태그 클릭:', tag.name)}
                                >
                                  {tag.name}
                                </span>
                              ))}
                              {article.tags.length > 8 && (
                                <span className="tag tag-gray">
                                  +{article.tags.length - 8}개 더
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* AI 추출 키워드 */}
                        {article.keywords && article.keywords.length > 0 && (
                          <div className="metadata-section">
                            <span className="metadata-label">🔑 AI 키워드:</span>
                            <div className="tags">
                              {article.keywords.slice(0, 6).map((keyword) => (
                                <span key={keyword.id} className="tag tag-yellow">
                                  {keyword.keyword}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* AI 인식 개체명 */}
                        {article.entities && article.entities.length > 0 && (
                          <div className="metadata-section">
                            <span className="metadata-label">👤 AI 개체명:</span>
                            <div className="tags">
                              {article.entities.slice(0, 4).map((entity) => (
                                <span key={entity.id} className="tag tag-purple">
                                  {entity.entity_text} ({entity.entity_type})
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'create' && (
            <ArticleCreateForm onSuccess={() => setActiveTab('articles')} />
          )}
        </div>
      </main>
    </div>
  );
}

// 대시보드 컴포넌트
const DashboardContent = ({ stats }) => {
  const [keywords, setKeywords] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const [keywordsData, tagsData] = await Promise.all([
          api.request('/keywords/top?limit=15'),
          api.request('/tags/')
        ]);

        if (keywordsData) setKeywords(keywordsData);
        if (tagsData) setTags(tagsData);
      } catch (error) {
        console.error('대시보드 데이터 로드 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  if (!stats) {
    return (
      <div className="loading">
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
        <h2>대시보드 데이터를 불러오는 중...</h2>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontSize: '1.5rem' }}>
          📊 시스템 대시보드
        </h2>
        <p style={{ color: '#64748b', fontSize: '1rem' }}>
          AI 기반 정치 기사 분석 시스템의 전체 현황을 확인할 수 있습니다
        </p>
      </div>

      {/* 메인 통계 그리드 */}
      <div className="stats-grid">
        {/* 카테고리별 분포 차트 */}
        <div className="stat-card">
          <h3>📈 카테고리별 기사 분포</h3>

          {stats.category_distribution && stats.category_distribution.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {stats.category_distribution.map((cat) => {
                const total = stats.total_articles;
                const percentage = total > 0 ? ((cat.count / total) * 100).toFixed(1) : 0;
                const categoryColor = getCategoryColor(cat.category);

                return (
                  <div key={cat.category} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '3px',
                          backgroundColor: categoryColor
                        }}></div>
                        <span style={{ fontWeight: '500', color: '#374151' }}>{cat.category}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>{percentage}%</span>
                        <span style={{ fontWeight: '600', color: '#1f2937' }}>{cat.count}개</span>
                      </div>
                    </div>

                    {/* 프로그레스 바 */}
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${percentage}%`,
                          background: `linear-gradient(90deg, ${categoryColor}, ${categoryColor}bb)`
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📄</div>
              <p>기사 데이터가 없습니다</p>
            </div>
          )}
        </div>

        {/* AI 모델 활성 상태 */}
        <div className="stat-card">
          <h3>🤖 활성 AI 모델</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {stats.ai_models_active?.map((model, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem',
                backgroundColor: '#f0fdf4',
                borderRadius: '8px',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#22c55e',
                  marginRight: '0.75rem'
                }}></div>
                <span style={{ color: '#15803d', fontWeight: '500' }}>{model}</span>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            backgroundColor: '#eff6ff',
            borderRadius: '6px',
            fontSize: '0.875rem',
            color: '#1e40af'
          }}>
            💡 모든 AI 모델이 정상 작동 중입니다
          </div>
        </div>
      </div>

      {/* 인기 트렌드 섹션 */}
      <div className="trending-dashboard">
        {/* 인기 키워드 TOP 15 */}
        <div className="card">
          <h3>
            🔥 인기 키워드 TOP 15
          </h3>

          {loading ? (
            <div className="loading">
              <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔄</div>
              <p>키워드 데이터 로딩 중...</p>
            </div>
          ) : keywords.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔍</div>
              <p>키워드 데이터가 없습니다</p>
            </div>
          ) : (
            <div className="keyword-list">
              {keywords.slice(0, 15).map((keyword, index) => (
                <div key={keyword.keyword} className={`keyword-item ${index < 5 ? 'top' : ''}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: index < 5 ? '#f59e0b' : '#64748b',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {index + 1}
                    </span>
                    <span style={{ fontWeight: index < 5 ? '600' : '500', color: '#1f2937' }}>
                      {keyword.keyword}
                    </span>
                  </div>
                  <div className="keyword-stats">
                    <span>
                      {keyword.frequency}회 등장
                    </span>
                    <span className="importance">
                      중요도 {keyword.avg_importance}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 인기 태그 클라우드 */}
        <div className="card">
          <h3>
            🏷️ 인기 태그
          </h3>

          {loading ? (
            <div className="loading">
              <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🔄</div>
              <p>태그 데이터 로딩 중...</p>
            </div>
          ) : tags.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🏷️</div>
              <p>태그 데이터가 없습니다</p>
            </div>
          ) : (
            <div>
              <div className="tags-container">
                {tags.slice(0, 20).map((tag, index) => {
                  const tagClass = index < 3 ? 'tag-blue' : index < 8 ? 'tag-purple' : 'tag-gray';

                  return (
                    <span
                      key={tag.tag}
                      className={`tag ${tagClass} clickable`}
                      style={{
                        fontSize: index < 3 ? '1rem' : index < 8 ? '0.875rem' : '0.75rem',
                        fontWeight: index < 3 ? '700' : index < 8 ? '600' : '500'
                      }}
                    >
                      {tag.tag} <span style={{ fontSize: '0.7em', opacity: 0.7 }}>({tag.count})</span>
                    </span>
                  );
                })}
              </div>

              <div className="info-box">
                <div>
                  <strong>키워드 추출:</strong> KoNLPy AI를 사용해 한국어 형태소 분석으로 중요한 명사들을 추출하고 빈도수 기반으로 중요도를 계산
                </div>
                <div style={{ marginTop: '0.5rem' }}>
                  <strong>태그 생성:</strong> Rule Base Model을 사용해 키워드 매칭, AI 분류 카테고리, 추출된 키워드를 생성
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 시스템 요약 정보 */}
      <div className="stat-card" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem' }}>🎯 시스템 요약</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <div>
            <div style={{ color: 'white', fontSize: '2rem', fontWeight: '700', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
              {stats.total_articles}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: 0.95 }}>총 분석 완료 기사</div>
          </div>
          <div>
            <div style={{ color: 'white', fontSize: '2rem', fontWeight: '700', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
              {stats.total_tags}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: 0.95 }}>AI 생성 태그</div>
          </div>
          <div>
            <div style={{ color: 'white', fontSize: '2rem', fontWeight: '700', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
              {stats.total_keywords}
            </div>
            <div style={{ fontSize: '0.9rem', opacity: 0.95 }}>추출된 키워드</div>
          </div>
          <div>
            <div style={{ color: 'white', fontSize: '2rem', fontWeight: '700', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
              100%
            </div>
            <div style={{ fontSize: '0.9rem', opacity: 0.95 }}>AI 자동화율</div>
          </div>
        </div>

      </div>
    </div>
  );
};

// 기사 작성 폼 컴포넌트
const ArticleCreateForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    source: ''
  });
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const analyzeOnly = async () => {
    if (!formData.title || !formData.content) {
      showNotification('제목과 내용을 입력해주세요.', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/v1/articles/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: formData.title,
          content: formData.content
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      setAnalysisResult(result);
      showNotification(`🤖 AI 분석 완료! (${result.processing_time.toFixed(2)}초)`);
    } catch (error) {
      console.error('Analysis failed:', error);
      showNotification('AI 분석에 실패했습니다. 백엔드 상태를 확인해주세요.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createArticle = async () => {
    if (!formData.title || !formData.content) {
      showNotification('제목과 내용을 입력해주세요.', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/v1/articles/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      showNotification(`🎉 기사가 성공적으로 생성되었습니다! (ID: ${result.article_id})`);

      setFormData({ title: '', content: '', source: '' });
      setAnalysisResult(null);

      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Article creation failed:', error);
      showNotification('기사 생성에 실패했습니다. 백엔드 상태를 확인해주세요.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* 알림 */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <h2>✏️ 새 기사 생성 및 AI 분석</h2>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        🤖 AI가 자동으로 요약, 카테고리, 키워드, 개체명, 태그를 생성합니다
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div className="form-group">
          <label htmlFor="title">기사 제목 *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="정치 기사 제목을 입력하세요"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="content">기사 내용 *</label>
          <textarea
            id="content"
            name="content"
            value={formData.content}
            onChange={handleChange}
            placeholder="기사 내용을 입력하세요..."
            rows={8}
            className="form-textarea"
          />
        </div>

        <div className="form-group">
          <label htmlFor="source">출처 (선택사항)</label>
          <input
            type="text"
            id="source"
            name="source"
            value={formData.source}
            onChange={handleChange}
            placeholder="예: 연합뉴스, 청와대, KBS 등"
            className="form-input"
          />
        </div>

        <div className="btn-group">
          <button
            onClick={analyzeOnly}
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? '🔄' : '🔍'}
            AI 분석만 수행
          </button>

          <button
            onClick={createArticle}
            disabled={loading}
            className="btn btn-success"
          >
            {loading ? '🔄' : '💾'}
            기사 생성 및 저장
          </button>
        </div>
      </div>

      {/* AI 분석 결과 표시 */}
      {analysisResult && (
        <div className="analysis-result">
          <h3>🤖 AI 분석 결과 및 생성 원리</h3>

          <div className="analysis-grid">
            <div className="analysis-item">
              <label>📝 AI 요약 (BART 모델)</label>
              <p className="analysis-content">
                {analysisResult.generated_metadata.summary}
              </p>
              <small style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic' }}>
                💡 전체 기사를 이해하고 핵심 문장들을 추출하여 자연스러운 요약을 생성했습니다.
              </small>
            </div>

            <div className="analysis-item">
              <label>📂 카테고리 분류 (규칙 기반)</label>
              <p className="analysis-content">
                {analysisResult.generated_metadata.category}
              </p>
            </div>

            <div className="analysis-item">
              <label>⏱️ 처리 시간</label>
              <p className="analysis-content">
                {analysisResult.processing_time.toFixed(2)}초
              </p>
            </div>
          </div>

          <div className="analysis-item">
            <label>🔑 키워드 추출 (KoNLPy AI)</label>
            <div className="tags">
              {analysisResult.generated_metadata.keywords.map((keyword, index) => (
                <span key={index} className="tag tag-blue">
                  {keyword}
                </span>
              ))}
            </div>
            <small style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic', marginTop: '0.5rem', display: 'block' }}>
              💡 한국어 형태소 분석을 통해 의미있는 명사들을 추출하고 중요도순으로 정렬했습니다.
            </small>
          </div>

          <div className="analysis-item">
            <label>👤 개체명 인식 (BERT NER)</label>
            <div className="tags">
              {analysisResult.generated_metadata.entities.map((entity, index) => (
                <span key={index} className="tag tag-purple">
                  {entity.entity_text} ({entity.entity_type})
                </span>
              ))}
            </div>
            <small style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic', marginTop: '0.5rem', display: 'block' }}>
              💡 문맥을 분석하여 인명(PERSON), 기관명(ORG), 지역명(LOC) 등을 자동으로 식별했습니다.
            </small>
          </div>

          <div className="analysis-item">
            <label>🏷️ 태그 생성 (규칙 + AI 조합)</label>
            <div className="tags">
              {analysisResult.generated_metadata.tags.map((tag, index) => (
                <span key={index} className="tag tag-green">
                  {tag}
                </span>
              ))}
            </div>
            <small style={{ fontSize: '0.75rem', color: '#6b7280', fontStyle: 'italic', marginTop: '0.5rem', display: 'block' }}>
              💡 카테고리와 추출된 키워드들을 조합하여 검색과 분류에 유용한 해시태그를 생성했습니다.
            </small>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;